<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BulkLabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/BulkLabelActions.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'removeLabels',
  'toggleAll',
  'deleteSelected',
]);

const { t } = useI18n();

const selectedCount = computed(() => props.selectedContactIds.length);
const totalVisibleContacts = computed(() => props.visibleContactIds.length);

const selectAllLabel = computed(() => {
  if (!totalVisibleContacts.value) {
    return '';
  }

  return t('CONTACTS_BULK_ACTIONS.SELECT_ALL', {
    count: totalVisibleContacts.value,
  });
});

const selectedCountLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allVisibleContactsSelected = computed(
  () =>
    totalVisibleContacts.value > 0 &&
    props.visibleContactIds.every(id => props.selectedContactIds.includes(id))
);

const handleAssignLabels = labels => {
  emit('assignLabels', labels);
};

const handleRemoveLabels = labels => {
  emit('removeLabels', labels);
};
</script>

<template>
  <div
    class="sticky top-0 z-10 bg-gradient-to-b from-n-surface-1 from-90% to-transparent pt-1 pb-2"
  >
    <div class="flex items-center gap-2 py-2 ltr:pr-3 rtl:pl-3">
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <Checkbox
          :model-value="allVisibleContactsSelected"
          :aria-label="selectAllLabel"
          @change="event => emit('toggleAll', event.target.checked)"
        />
        <span>{{ selectedCountLabel }}</span>
      </label>
      <div class="flex items-center gap-2 ml-auto">
        <Button
          sm
          ghost
          slate
          :label="t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1"
          @click="emit('clearSelection')"
        />
        <BulkLabelActions
          type="contact"
          :is-loading="isLoading"
          :disabled="!selectedCount"
          @assign="handleAssignLabels"
        />
        <BulkLabelActions
          type="contact"
          action="remove"
          :is-loading="isLoading"
          :disabled="!selectedCount"
          @remove="handleRemoveLabels"
        />
        <div class="w-px h-3 bg-n-weak rounded-lg" />
        <Policy :permissions="['administrator']">
          <Button
            v-tooltip.bottom="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
            sm
            ghost
            ruby
            icon="i-lucide-trash"
            :label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
            :aria-label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
            :disabled="!selectedCount || isLoading"
            :is-loading="isLoading"
            class="!px-2 [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex"
            @click="emit('deleteSelected')"
          />
        </Policy>
      </div>
    </div>
  </div>
</template>
